import { AxiosInstance } from 'axios';
import { createInstance } from '../service';
import { IPersistenceStorage } from '../TokenStorage';
import { APITokens, TokenControllerSettings } from '../types';
import { EsiaAuthData, EsiaAuthResponse, EsiaDataResponse } from './types';

interface PostLogoutData extends APITokens {
  oid: string;
}

export class ESIAService {
  private _axios: AxiosInstance;
  private _isLogoutCompleteKey = 'is_logout_complete_key';
  private _accessTokenKey = 'esia_logout_access_token_snapshot';
  private _refreshTokenKey = 'esia_logout_refresh_token_snapshot';
  private _oidKey = 'esia_logout_oid_snapshot';

  constructor(
    settings: TokenControllerSettings,
    private _persistenceStorage: IPersistenceStorage,
  ) {
    this._axios = createInstance(settings.basePath);
  }

  private async _data(code: string, state: string): Promise<EsiaDataResponse> {
    const response = await this._axios.get<EsiaDataResponse>('esia/data/', {
      params: { code, state },
    });

    // @ts-ignore
    return response.data;
  }

  private async _auth(data: EsiaDataResponse): Promise<EsiaAuthResponse> {
    const response = await this._axios.post<EsiaAuthResponse>(
      // TODO: В Туристе используется другой эндпоинт. Если делать другой эндпоинт
      'esia-auth-open/',
      data,
    );

    // @ts-ignore
    return response.data;
  }

  getEsiaLoginLink = async () => {
    const response = await this._axios.get<{ link: string }>(`esia/link/`);

    // @ts-ignore
    return response.data.link;
  };

  handleEsiaLogin = async (
    url: string,
    onLogin: (tokens: EsiaAuthData) => Promise<void> | void,
    /** Колбек, который вызывается, если пользователь не принимал условия политики конфиденциальности и пытается выполнить вход в свой аккаунт */
    onPrivacyPolicyNotAgreed: (access: string, refresh: string) => void,
    /**
     * После успешного входа необходимо выполнить логаут с аккаунта пользователя ЕСИА.
     * Логаут выполняется с помощью перехода на логаут URL.
     *
     * Логаут URL имеет следующий вид "https://esia.gosuslugi.ru/idp/ext/Logout?client_id=2....1&redirect_url=https://example.com/"
     */
    redirectToLogoutURL: (link: string) => void,
  ) => {
    const postLogoutData = this._checkPostLogout();

    if (postLogoutData) {
      const { access, refresh, oid } = postLogoutData;

      const accepted = await this._checkPrivacyPolicyAgreement(access);

      if (!accepted) {
        onPrivacyPolicyNotAgreed(access, refresh);
      } else {
        onLogin({ access, refresh, oid: oid });
      }
    }

    const { searchParams } = new URL(url);

    const error = searchParams.get('error');

    if (error) throw new Error(error);

    const code = searchParams.get('code');
    const state = searchParams.get('state');

    if (!code || !state) return;

    const data = await this._data(code, state);

    const { access, refresh } = await this._auth(data);

    this._setLogoutFlag();
    this._setPostLogoutData({ access, refresh, oid: data.oid });

    const logoutURL = await this._logout();

    redirectToLogoutURL(logoutURL);
  };

  private async _logout(): Promise<string> {
    const response = await this._axios.get<{ link: string }>(`esia/logout/`);

    // @ts-ignore
    return response.data.link;
  }

  async _checkPrivacyPolicyAgreement(access: string): Promise<boolean> {
    const response = await this._axios.get('user-profile-open/', {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + access,
      },
    });

    const { is_privacy_policy_accepted } = response.data;

    return is_privacy_policy_accepted;
  }

  private _checkPostLogout = (): PostLogoutData | undefined => {
    if (!this._isPostLogout()) return;

    const { access, refresh, oid } = this._getPostLogoutData();

    this._clearLogoutFlag();
    this._clearPostLogoutData();

    return { access, refresh, oid };
  };

  /**
   * Логаут из аккаунта ЕСИА выполняется редиректом на сайт ЕСИА и последующим редиректом на url, указанный в redirect_url в настройках ЕСИА.
   * Т.е требуется хранить данные в постоянном хранилище, которое сможет хранить данные после редиректов (После редиректа браузер открывает НОВУЮ страницу)
   */
  private _setPostLogoutData = ({ access, refresh, oid }: PostLogoutData) => {
    this._persistenceStorage.set(this._accessTokenKey, access);
    this._persistenceStorage.set(this._refreshTokenKey, refresh);
    this._persistenceStorage.set(this._oidKey, oid);
  };

  private _getPostLogoutData = (): PostLogoutData => {
    const access = this._persistenceStorage.get(this._accessTokenKey);
    const refresh = this._persistenceStorage.get(this._refreshTokenKey);
    const oid = this._persistenceStorage.get(this._oidKey);

    if (!access || !refresh || !oid) {
      const msg =
        'Ошибка пост-логаутной аутентификации: не удалось получить данные для входа';

      throw new Error(msg);
    }

    return { access, refresh, oid };
  };

  private _clearPostLogoutData = () => {
    this._persistenceStorage.clear(this._accessTokenKey);
    this._persistenceStorage.clear(this._refreshTokenKey);
    this._persistenceStorage.clear(this._oidKey);
  };

  private _setLogoutFlag = () => {
    this._persistenceStorage.set(this._isLogoutCompleteKey, 'true');
  };

  private _clearLogoutFlag = () => {
    this._persistenceStorage.clear(this._isLogoutCompleteKey);
  };

  private _isPostLogout = () => {
    return !!this._persistenceStorage.get(this._isLogoutCompleteKey);
  };
}
